import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const AppleLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;
  const cust = quote.customerData || {};
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 980, background: '#f5f5f7', border: 'none', color: '#1d1d1f', cursor: 'pointer', fontSize: 12 };

  const Row = ({ service, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '20px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.45 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isOpt && <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 18, height: 18, accentColor: theme.primary, cursor: 'pointer' }} />}
              <p style={{ fontSize: 19, fontWeight: 600, color: theme.textPrimary, margin: 0, letterSpacing: '-0.2px' }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 15, color: theme.textSecondary, marginTop: 6, lineHeight: 1.5 }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 17, fontWeight: 600, color: theme.textPrimary }}>{formatEUR(lineTotal(service))}</span>
            {suffix && <div style={{ fontSize: 12, color: theme.textMuted }}>{suffix}</div>}
          </div>
        </div>
      </div>
    );
  };
  const Group = ({ title, services, suffix }) => services.length ? (
    <div style={{ marginTop: 8 }}>
      <p style={{ fontSize: 13, fontWeight: 600, color: theme.textMuted, textAlign: 'center', marginBottom: 4 }}>{title}</p>
      {services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} />)}
    </div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 80px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '18px' }}>
        <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#34c759' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      {/* HERO */}
      <div style={{ textAlign: 'center', padding: '60px 24px 50px', maxWidth: 700, margin: '0 auto' }}>
        {company?.logo && <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 30, marginBottom: 34, objectFit: 'contain' }} />}
        {cust.company && <p style={{ fontSize: 17, color: theme.textSecondary, marginBottom: 8 }}>Für {cust.company}</p>}
        <h1 style={{ fontSize: 'clamp(28px, 8.5vw, 48px)', lineHeight: 1.05, fontWeight: 600, letterSpacing: '-1.5px', margin: 0, color: theme.textPrimary }}>
          {quote.heroHeadline || quote.quoteTitle || 'Ihr neuer Auftritt.'}
        </h1>
        <p style={{ fontSize: 'clamp(28px, 8.5vw, 48px)', lineHeight: 1.05, fontWeight: 600, letterSpacing: '-1.5px', margin: 0, color: theme.textSecondary }}>
          {quote.heroSubline || 'Beeindruckend einfach.'}
        </p>
        {quote.quoteIntro && <p style={{ fontSize: 19, color: theme.textSecondary, marginTop: 26, lineHeight: 1.5, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto', whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</p>}
        <div style={{ marginTop: 30 }}>
          <span style={{ fontSize: 21, color: theme.textPrimary }}>Ab </span>
          <span style={{ fontSize: 21, fontWeight: 600, color: theme.textPrimary }}>{formatEUR(einmaligTotals.brutto)}</span>
        </div>
        <button onClick={() => setShowAcceptanceForm(true)} className="no-print" style={{ marginTop: 22, padding: '12px 28px', borderRadius: 980, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 17 }}>
          Angebot annehmen
        </button>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 26 }}>
        {quote.customerArguments && (
          <div style={{ textAlign: 'center', padding: '24px 0', borderTop: `1px solid ${theme.surfaceBorder}`, borderBottom: `1px solid ${theme.surfaceBorder}` }}>
            <div style={{ fontSize: 17, color: theme.textSecondary, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}
        {quote.videoUrl && <div className="no-print"><video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius }} /></div>}

        <div>
          <Group title="Einmalige Leistungen" services={einmaligServices} suffix="" />
          <Group title="Monatliche Leistungen" services={monatlichServices} suffix="/ Monat" />
          <Group title="Jährliche Leistungen" services={jaehrlichServices} suffix="/ Jahr" />
        </div>

        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <p style={{ fontSize: 15, color: theme.textMuted, margin: 0 }}>Gesamt inkl. MwSt.</p>
          <p style={{ fontSize: 'clamp(30px, 8vw, 44px)', fontWeight: 600, letterSpacing: '-1px', margin: '6px 0 0', color: theme.textPrimary }}>{formatEUR(einmaligTotals.brutto)}</p>
          {(monatlichTotals.brutto > 0 || jaehrlichTotals.brutto > 0) && (
            <p style={{ fontSize: 16, color: theme.textSecondary, marginTop: 8 }}>
              {monatlichTotals.brutto > 0 && <>zzgl. {formatEUR(monatlichTotals.brutto)} / Monat </>}
              {jaehrlichTotals.brutto > 0 && <>· {formatEUR(jaehrlichTotals.brutto)} / Jahr</>}
            </p>
          )}
        </div>

        <TermsBlock quote={quote} company={company} theme={theme} />
        <AcceptanceBlock ctx={ctx} theme={theme} />
        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FeedbackBlock viewHash={viewHash} theme={theme} />
          <QuestionBlock viewHash={viewHash} theme={theme} />
        </div>
        <FooterBlock company={company} theme={theme} />
      </div>
    </div>
  );
};

export default AppleLayout;
